import {BOLD_MARKER, CODE_MARKER, ITALIC_MARKER, STRIKETHROUGH_MARKER} from './composerFormatting';

export interface ClipboardPayload {
    html?: string;
    text: string;
}

export type PasteKind = 'table' | 'rich' | 'plain';

export interface ConvertPasteResult {
    text: string;
    kind: PasteKind;
    tableSource?: 'html' | 'text';
}

const ZERO_WIDTH_SPACE = '\u200B';

const MAX_TABLE_TEXT_LENGTH = 256 * 1024;
const MAX_TABLE_ROWS = 2000;

const collapseWhitespace = (value: string): string => value.replace(/[\t\n\r\f\v\u00A0 ]+/g, ' ');

const collapseBlankLineRuns = (value: string): string => value.replace(/\n{3,}/g, '\n\n');

const max = (values: number[], fallback = 0): number => values.reduce((acc, value) => (value > acc ? value : acc), fallback);

function parseHtmlDocument(html: string): Document {
    return new DOMParser().parseFromString(html, 'text/html');
}

const CELL_BLOCK_BOUNDARY_TAGS = new Set(['BR', 'DIV', 'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI']);

function cellPlainText(cell: Element): string {
    let result = '';
    const walk = (node: Node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            result += node.textContent || '';
            return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) {
            return;
        }
        const el = node as Element;
        if (CELL_BLOCK_BOUNDARY_TAGS.has(el.tagName)) {
            result += ' ';
            if (el.tagName !== 'BR') {
                Array.from(el.childNodes).forEach(walk);
                result += ' ';
            }
            return;
        }
        Array.from(el.childNodes).forEach(walk);
    };
    Array.from(cell.childNodes).forEach(walk);
    return result;
}

function cellTextFromElement(cell: Element): string {
    return collapseWhitespace(cellPlainText(cell)).trim();
}

function directChildrenByTag(el: Element, tags: string[]): Element[] {
    return Array.from(el.children).filter(child => tags.includes(child.tagName));
}

function directTableRowElements(table: Element): Element[] {
    const sections = directChildrenByTag(table, ['THEAD', 'TBODY', 'TFOOT']);
    const containers = sections.length > 0 ? sections : [table];
    return containers.flatMap(container => directChildrenByTag(container, ['TR']));
}

function directRowCellElements(row: Element): Element[] {
    return directChildrenByTag(row, ['TD', 'TH']);
}

interface CarrySlot {
    el: Element;
    remaining: number;
}

function extractTableCellElements(table: Element): Array<Array<Element | null>> {
    const rowEls = directTableRowElements(table);
    const carry: Array<CarrySlot | null> = [];
    const rows: Array<Array<Element | null>> = [];

    rowEls.forEach(tr => {
        const row: Array<Element | null> = [];
        const cells = directRowCellElements(tr);
        let cellIndex = 0;
        let column = 0;
        let guard = 0;

        while ((cellIndex < cells.length || carry.slice(column).some(Boolean)) && guard < 1000) {
            guard += 1;
            const carried = carry[column];
            if (carried) {
                row[column] = carried.el;
                carried.remaining -= 1;
                if (carried.remaining <= 0) {
                    carry[column] = null;
                }
                column += 1;
                continue;
            }

            if (cellIndex >= cells.length) {
                break;
            }

            const cell = cells[cellIndex++];
            const colspan = Math.max(1, parseInt(cell.getAttribute('colspan') || '1', 10) || 1);
            const rowspan = Math.max(1, parseInt(cell.getAttribute('rowspan') || '1', 10) || 1);
            for (let s = 0; s < colspan; s++) {
                row[column] = s === 0 ? cell : null;
                if (rowspan > 1 && s === 0) {
                    carry[column] = {el: cell, remaining: rowspan - 1};
                }
                column += 1;
            }
        }

        rows.push(row);
    });

    const columns = max(rows.map(row => row.length));
    return rows.map(row => {
        const padded = [...row];
        while (padded.length < columns) {
            padded.push(null);
        }
        return padded;
    });
}

function findTopLevelTables(root: Element): Element[] {
    const found: Element[] = [];
    const walk = (node: Element) => {
        Array.from(node.children).forEach(child => {
            if (child.tagName === 'TABLE') {
                found.push(child);
                return;
            }
            walk(child);
        });
    };
    walk(root);
    return found;
}

const NON_TEXTUAL_TAGS = new Set(['STYLE', 'SCRIPT', 'META', 'LINK', 'NOSCRIPT']);

function isFragmentOutsideTableBlank(body: Element, table: Element): boolean {
    let outsideText = '';
    const walk = (node: Node) => {
        if (node === table) {
            return;
        }
        if (node.nodeType === Node.TEXT_NODE) {
            outsideText += node.textContent || '';
            return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) {
            return;
        }
        if (NON_TEXTUAL_TAGS.has((node as Element).tagName)) {
            return;
        }
        Array.from(node.childNodes).forEach(walk);
    };
    Array.from(body.childNodes).forEach(walk);
    return outsideText.trim() === '';
}

function cellElementsToText(rows: Array<Array<Element | null>>): string[][] {
    return rows.map(row => row.map(cell => (cell ? cellTextFromElement(cell) : '')));
}

export function detectHtmlTable(html: string): string[][] | null {
    if (html.length > MAX_TABLE_TEXT_LENGTH) {
        return null;
    }

    const doc = parseHtmlDocument(html);
    const topLevelTables = findTopLevelTables(doc.body);
    if (topLevelTables.length !== 1) {
        return null;
    }

    const table = topLevelTables[0];
    if (!isFragmentOutsideTableBlank(doc.body, table)) {
        return null;
    }

    const cellRows = extractTableCellElements(table);
    if (cellRows.length > MAX_TABLE_ROWS) {
        return null;
    }
    const rows = cellElementsToText(cellRows);

    if (rows.length < 2) {
        return null;
    }
    const columns = max(rows.map(row => row.length));
    if (columns < 2) {
        return null;
    }
    if (!rows.some(row => row.some(cell => cell !== ''))) {
        return null;
    }

    return rows;
}

function parseDelimited(text: string, delimiter: string): string[][] | null {
    if (!text.includes(delimiter)) {
        return null;
    }

    const rows: string[][] = [];
    let row: string[] = [];
    let field = '';
    let inQuotes = false;
    let sawAnyContent = false;

    const pushField = () => {
        row.push(field.trim());
        field = '';
    };
    const pushRow = () => {
        pushField();
        rows.push(row);
        row = [];
    };

    for (let i = 0; i < text.length; i++) {
        if (rows.length > MAX_TABLE_ROWS) {
            return null;
        }
        const ch = text[i];

        if (inQuotes) {
            if (ch === '"') {
                if (text[i + 1] === '"') {
                    field += '"';
                    i++;
                } else {
                    inQuotes = false;
                }
            } else {
                field += ch;
            }
            continue;
        }

        if (ch === '"' && field.trim() === '') {
            inQuotes = true;
            sawAnyContent = true;
            continue;
        }
        if (ch === delimiter) {
            sawAnyContent = true;
            pushField();
            continue;
        }
        if (ch === '\r') {
            continue;
        }
        if (ch === '\n') {
            sawAnyContent = true;
            pushRow();
            continue;
        }
        sawAnyContent = true;
        field += ch;
    }

    if (field !== '' || row.length > 0) {
        pushRow();
    }

    return sawAnyContent ? rows : null;
}

const MARKDOWN_SEPARATOR_CELL = /^:?-+:?$/;

function isMarkdownSeparatorRow(row: string[]): boolean {
    return row.length > 0 && row.every(cell => cell.trim() === '' || MARKDOWN_SEPARATOR_CELL.test(cell.trim()));
}

function isSeparatorLikeLine(line: string): boolean {
    return line.includes('-') && /^[|:\-\s]+$/.test(line);
}

function parseMarkdownTable(text: string): string[][] | null {
    if (!text.includes('|')) {
        return null;
    }

    const lines = text.split(/\r\n|\r|\n/).filter(line => line.trim() !== '');
    if (lines.length === 0) {
        return null;
    }

    const rows = lines.map(line => {
        let stripped = line.trim();
        if (stripped.startsWith('|')) {
            stripped = stripped.slice(1);
        }
        if (stripped.endsWith('|')) {
            stripped = stripped.slice(0, -1);
        }
        return stripped.split('|').map(cell => cell.trim());
    });

    return rows.filter(row => !isMarkdownSeparatorRow(row));
}

function isRowEmpty(row: string[]): boolean {
    return row.every(cell => cell.trim() === '');
}

function trimTrailingEmptyFields(row: string[]): string[] {
    const copy = [...row];
    while (copy.length > 0 && copy[copy.length - 1].trim() === '') {
        copy.pop();
    }
    return copy;
}

function reconcileColumns(rows: string[][]): string[][] | null {
    const trimmedCounts = rows.map(row => trimTrailingEmptyFields(row).length);
    const columns = max(trimmedCounts);
    if (columns < 2) {
        return null;
    }
    if (!trimmedCounts.every(count => count === columns)) {
        return null;
    }
    return rows.map(row => {
        const sliced = row.slice(0, columns);
        while (sliced.length < columns) {
            sliced.push('');
        }
        return sliced;
    });
}

const CURRENCY_PATTERN = /^[$€£¥]\s*|\s*[$€£¥]$/g;
const THOUSANDS_GROUP_PATTERN = /(\d)[ \u00A0](?=\d{3}(\D|$))/g;

function isNumericCell(raw: string): boolean {
    let value = raw.trim();
    if (value === '') {
        return false;
    }
    value = value.replace(CURRENCY_PATTERN, '').replace(/%$/, '').trim();
    if (value === '') {
        return false;
    }
    value = value.replace(THOUSANDS_GROUP_PATTERN, '$1');
    return /^[+-]?\d{1,3}(?:[.,]\d{3})*(?:[.,]\d+)?$|^[+-]?\d+(?:[.,]\d+)?$/.test(value);
}

function looksLikeProse(rows: string[][]): boolean {
    const allCells = rows.flatMap(row => row.map(cell => cell.trim())).filter(cell => cell !== '');
    if (allCells.length === 0) {
        return false;
    }

    const maxLength = max(allCells.map(cell => cell.length));
    const averageLength = allCells.reduce((sum, cell) => sum + cell.length, 0) / allCells.length;
    if (maxLength > 60 || averageLength > 24) {
        return true;
    }

    const rowsEndingWithPunctuation = rows.filter(row => {
        const lastNonEmpty = [...row].reverse().find(cell => cell.trim() !== '');
        return !!lastNonEmpty && /[.!?]\s*$/.test(lastNonEmpty.trim());
    }).length;

    return rowsEndingWithPunctuation > rows.length / 2;
}

const MARKUP_LINE_START = /^[ \t]|^[-*]\s|^\d+\.\s|^[#>]\s/;
const CODE_LINE_END = /[;{},]\s*$/;
const CALL_SHAPE = /\w\([^)]*\)/;
const ASSIGNMENT_SHAPE = /\s=\s|:=/;
const STATEMENT_KEYWORD_START = /^(import|const|let|SELECT)\b/;
const IMPORT_FROM_SHAPE = /^import\b.*\bfrom\b/;
const BRACES = /[{}]/;
const SQUARE_BRACKETS = /[[\]]/;
const ARROW = /=>/;

function stripQuotedSegments(line: string): string {
    let result = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
            if (inQuotes && line[i + 1] === '"') {
                i++;
                continue;
            }
            inQuotes = !inQuotes;
            continue;
        }
        if (!inQuotes) {
            result += ch;
        }
    }
    return result;
}

function looksLikeCodeLine(line: string): boolean {
    const content = stripQuotedSegments(line);
    return (
        CODE_LINE_END.test(line) ||
        CALL_SHAPE.test(content) ||
        ASSIGNMENT_SHAPE.test(content) ||
        STATEMENT_KEYWORD_START.test(content) ||
        IMPORT_FROM_SHAPE.test(content) ||
        BRACES.test(content) ||
        SQUARE_BRACKETS.test(content) ||
        ARROW.test(content)
    );
}

function stripSharedTrailingDelimiter(lines: string[], delimiter: string): string[] {
    if (delimiter !== ',' || lines.length === 0) {
        return lines;
    }
    if (!lines.every(line => line.endsWith(delimiter))) {
        return lines;
    }
    return lines.map(line => line.slice(0, -delimiter.length));
}

const SHELL_WORDS = ['cat', 'grep', 'awk', 'sed', 'ls', 'find', 'sort', 'uniq', 'head', 'tail', 'xargs', 'echo', 'curl', 'wget', 'ps', 'kill', 'chmod', 'chown', 'tar', 'ssh', 'git', 'docker', 'kubectl', 'ping', 'cut', 'wc', 'tee'];
const SHELL_WORD_PATTERN = new RegExp(`\\b(${SHELL_WORDS.join('|')})\\b`);

function looksLikeShellPipe(line: string): boolean {
    return line.includes('|') && SHELL_WORD_PATTERN.test(line);
}

function significantRawLines(text: string): string[] {
    return text.split(/\r\n|\r|\n/).filter(line => line.trim() !== '' && !isSeparatorLikeLine(line.trim()));
}

interface DelimiterCandidate {
    delimiter: string;
    parse: (text: string) => string[][] | null;
    checkProse: boolean;
    checkMarkup: boolean;
    checkContent: boolean;
    checkShellPipe: boolean;
}

const DELIMITER_CANDIDATES: DelimiterCandidate[] = [
    {delimiter: '\t', parse: text => parseDelimited(text, '\t'), checkProse: false, checkMarkup: false, checkContent: false, checkShellPipe: false},
    {delimiter: ';', parse: text => parseDelimited(text, ';'), checkProse: true, checkMarkup: true, checkContent: true, checkShellPipe: false},
    {delimiter: ',', parse: text => parseDelimited(text, ','), checkProse: true, checkMarkup: true, checkContent: true, checkShellPipe: false},
    {delimiter: '|', parse: text => parseMarkdownTable(text), checkProse: false, checkMarkup: true, checkContent: true, checkShellPipe: true},
];

function meetsShape(delimiter: string, rowCount: number, colCount: number): boolean {
    if (delimiter === '\t') {
        return rowCount >= 2 && colCount >= 2;
    }
    return (rowCount >= 3 && colCount >= 2) || (rowCount >= 2 && colCount >= 3);
}

export function detectDelimitedTable(text: string): string[][] | null {
    if (text.length > MAX_TABLE_TEXT_LENGTH) {
        return null;
    }

    for (const candidate of DELIMITER_CANDIDATES) {
        const parsed = candidate.parse(text);
        if (!parsed || parsed.length > MAX_TABLE_ROWS) {
            continue;
        }

        const nonEmptyRows = parsed.filter(row => !isRowEmpty(row));
        if (nonEmptyRows.length < 2) {
            continue;
        }

        const reconciled = reconcileColumns(nonEmptyRows);
        if (!reconciled) {
            continue;
        }

        if (!meetsShape(candidate.delimiter, reconciled.length, reconciled[0].length)) {
            continue;
        }

        if (candidate.delimiter === '\t' && reconciled.some(row => row[0].trim() === '')) {
            continue;
        }

        const lines = significantRawLines(text);
        if (candidate.checkMarkup && lines.some(line => MARKUP_LINE_START.test(line))) {
            continue;
        }
        if (candidate.checkContent) {
            const contentLines = stripSharedTrailingDelimiter(lines, candidate.delimiter);
            if (contentLines.some(looksLikeCodeLine)) {
                continue;
            }
        }
        if (candidate.checkShellPipe && lines.some(looksLikeShellPipe)) {
            continue;
        }
        if (candidate.checkProse && looksLikeProse(reconciled)) {
            continue;
        }

        return reconciled;
    }

    return null;
}

const DASH_SEPARATOR_LINE = /^-+(?: {2,}-+)+$/;
const ALIGNED_TABLE_INDENT_LINE = /^[ \t]/;
const ALIGNED_TABLE_MARKUP_LINE = /^[-*]\s|^\d+\.\s|^[#>]\s/;

function isDashSeparatorLine(line: string): boolean {
    return DASH_SEPARATOR_LINE.test(line.trim());
}

function splitOnSpaceRuns(line: string): string[] {
    return line.split(/ {2,}/).map(field => field.trim());
}

function hasConsistentColumnBoundaries(lines: string[]): boolean {
    if (lines.length < 3) {
        return false;
    }
    const reconciled = reconcileColumns(lines.map(splitOnSpaceRuns));
    if (!reconciled || reconciled[0].length < 3) {
        return false;
    }
    return !looksLikeProse(reconciled);
}

function countAlignedColumns(line: string): number {
    return splitOnSpaceRuns(line).filter(field => field !== '').length;
}

function findValidSeparatorIndex(lines: string[]): number {
    const separatorIndex = lines.findIndex(isDashSeparatorLine);
    if (separatorIndex <= 0 || separatorIndex >= lines.length - 1) {
        return -1;
    }
    const headerColumns = countAlignedColumns(lines[separatorIndex - 1]);
    const separatorColumns = countAlignedColumns(lines[separatorIndex]);
    return headerColumns >= 2 && headerColumns === separatorColumns ? separatorIndex : -1;
}

function looksLikeAlignedTextTable(text: string): boolean {
    if (text.length > MAX_TABLE_TEXT_LENGTH) {
        return false;
    }
    const nonBlankLines = text.split(/\r\n|\r|\n/).filter(line => line.trim() !== '');
    if (nonBlankLines.length < 2 || nonBlankLines.length > MAX_TABLE_ROWS) {
        return false;
    }

    const hasValidSeparator = findValidSeparatorIndex(nonBlankLines) !== -1;

    const nonSeparatorLines = nonBlankLines.filter(line => !isDashSeparatorLine(line));
    if (nonSeparatorLines.some(line => ALIGNED_TABLE_MARKUP_LINE.test(line))) {
        return false;
    }
    if (!hasValidSeparator && nonBlankLines.some(line => ALIGNED_TABLE_INDENT_LINE.test(line))) {
        return false;
    }

    const contentLines = nonBlankLines.filter(line => !isSeparatorLikeLine(line.trim()));
    if (contentLines.some(looksLikeCodeLine)) {
        return false;
    }

    if (hasValidSeparator) {
        return true;
    }

    return hasConsistentColumnBoundaries(nonBlankLines);
}

export function normalizeLineEndings(text: string): string {
    return text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
}

function detectAlignedTextTable(text: string): string | null {
    return looksLikeAlignedTextTable(text) ? fenceBlock(normalizeLineEndings(text)) : null;
}

interface TableDetectionResult {
    rows: string[][];
    source: 'html' | 'text';
}

function detectTableWithSource(clipboard: ClipboardPayload): TableDetectionResult | null {
    if (clipboard.html) {
        const fromHtml = detectHtmlTable(clipboard.html);
        if (fromHtml) {
            return {rows: fromHtml, source: 'html'};
        }
    }
    const fromText = detectDelimitedTable(clipboard.text);
    if (fromText) {
        return {rows: fromText, source: 'text'};
    }
    return null;
}

export function detectTable(clipboard: ClipboardPayload): string[][] | null {
    return detectTableWithSource(clipboard)?.rows ?? null;
}

const SPREADSHEET_HTML_MARKERS = [
    'urn:schemas-microsoft-com:office:excel',
    'google-sheets-html-origin',
    'sdval',
];

export function looksLikeSpreadsheetHtml(html: string): boolean {
    if (/<img\b/i.test(html)) {
        return false;
    }
    if (/<table\b/i.test(html)) {
        return true;
    }
    return SPREADSHEET_HTML_MARKERS.some(marker => html.includes(marker));
}

function trimTrailingEmptyRowsAndColumns(rows: string[][]): string[][] {
    const result = rows.map(row => [...row]);
    while (result.length > 0 && isRowEmpty(result[result.length - 1])) {
        result.pop();
    }
    if (result.length === 0) {
        return [];
    }

    const maxColumns = max(result.map(row => row.length));
    let lastNonEmptyColumn = -1;
    for (let column = 0; column < maxColumns; column++) {
        if (result.some(row => (row[column] ?? '').trim() !== '')) {
            lastNonEmptyColumn = column;
        }
    }

    return result.map(row => {
        const sliced = row.slice(0, lastNonEmptyColumn + 1);
        while (sliced.length < lastNonEmptyColumn + 1) {
            sliced.push('');
        }
        return sliced;
    });
}

const BARE_FENCE_LINE = /^```/;

function neutralizeCell(cell: string): string {
    const flattened = collapseWhitespace(cell).trim();
    return BARE_FENCE_LINE.test(flattened) ? flattened.replace(/`/g, `\`${ZERO_WIDTH_SPACE}`) : flattened;
}

const FENCE_OPENER_LINE = /^```[^`]*$/;

function isAlreadyFencedBlock(text: string): boolean {
    const lines = text.split('\n');
    return lines.length >= 2 && FENCE_OPENER_LINE.test(lines[0].trim()) && lines[lines.length - 1].trim() === '```';
}

function fenceBlock(rawText: string): string {
    const trimmed = rawText.replace(/\n+$/, '');
    return isAlreadyFencedBlock(trimmed) ? trimmed : '```\n' + trimmed + '\n```';
}

function isWideCodePoint(codePoint: number): boolean {
    return (
        (codePoint >= 0x1100 && codePoint <= 0x115f) ||
        (codePoint >= 0x2e80 && codePoint <= 0x303e) ||
        (codePoint >= 0x3041 && codePoint <= 0x33ff) ||
        (codePoint >= 0x3400 && codePoint <= 0x4dbf) ||
        (codePoint >= 0x4e00 && codePoint <= 0x9fff) ||
        (codePoint >= 0xa000 && codePoint <= 0xa4cf) ||
        (codePoint >= 0xac00 && codePoint <= 0xd7a3) ||
        (codePoint >= 0xf900 && codePoint <= 0xfaff) ||
        (codePoint >= 0xff00 && codePoint <= 0xff60) ||
        (codePoint >= 0xffe0 && codePoint <= 0xffe6) ||
        (codePoint >= 0x1f300 && codePoint <= 0x1faff) ||
        (codePoint >= 0x20000 && codePoint <= 0x3fffd)
    );
}

export function displayWidth(value: string): number {
    let width = 0;
    for (const char of Array.from(value)) {
        const codePoint = char.codePointAt(0) ?? 0;
        if (codePoint === 0x200b) {
            continue;
        }
        width += isWideCodePoint(codePoint) ? 2 : 1;
    }
    return width;
}

function isNumericColumn(cells: string[][], column: number, hasHeader: boolean): boolean {
    const rows = hasHeader ? cells.slice(1) : cells;
    const values = rows.map(row => row[column] ?? '').filter(value => value.trim() !== '');
    if (values.length === 0) {
        return false;
    }
    return values.every(isNumericCell);
}

function formatRow(row: string[], widths: number[], numericColumns: boolean[]): string {
    return row
        .map((cell, index) => {
            const width = widths[index];
            const pad = Math.max(0, width - displayWidth(cell));
            const isLast = index === row.length - 1;

            if (numericColumns[index]) {
                return ' '.repeat(pad) + cell;
            }
            if (isLast) {
                return cell;
            }
            return cell + ' '.repeat(pad);
        })
        .join('  ')
        .replace(/\s+$/, '');
}

function buildTableLines(rows: string[][]): string[] {
    const trimmed = trimTrailingEmptyRowsAndColumns(rows);
    if (trimmed.length === 0 || trimmed[0].length === 0) {
        return [''];
    }

    const cells = trimmed.map(row => row.map(neutralizeCell));
    const columns = cells[0].length;
    const widths = Array.from({length: columns}, (_, column) => max(cells.map(row => displayWidth(row[column] ?? ''))));
    const hasHeader = cells.length >= 2;
    const numericColumns = Array.from({length: columns}, (_, column) => isNumericColumn(cells, column, hasHeader));

    const dataRows = cells.map(row => formatRow(row, widths, numericColumns));

    if (!hasHeader) {
        return dataRows;
    }

    const separator = widths.map(width => '-'.repeat(width)).join('  ').replace(/\s+$/, '');
    return [dataRows[0], separator, ...dataRows.slice(1)];
}

export function formatTableAsCodeBlock(rows: string[][]): string {
    return '```\n' + buildTableLines(rows).join('\n') + '\n```';
}

export function formatTableBody(rows: string[][]): string {
    return buildTableLines(rows).join('\n');
}

type StyleFlags = {
    bold: boolean;
    italic: boolean;
    strike: boolean;
    code: boolean;
};

type RenderContext = StyleFlags & {inListItem: boolean; insideTableCell: boolean};

const EMPTY_CONTEXT: RenderContext = {bold: false, italic: false, strike: false, code: false, inListItem: false, insideTableCell: false};

interface EmitFlag {
    emitted: boolean;
}

const DROP_TAGS = new Set([
    'SCRIPT',
    'STYLE',
    'SVG',
    'VIDEO',
    'AUDIO',
    'IFRAME',
    'NOSCRIPT',
    'LINK',
    'META',
    'CANVAS',
    'OBJECT',
    'EMBED',
]);

const BLOCK_TAGS = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'SECTION', 'ARTICLE', 'LI']);

const MONOSPACE_KEYWORDS = [
    'monospace',
    'consolas',
    'courier',
    'menlo',
    'monaco',
    'source code',
    'roboto mono',
    'ubuntu mono',
    'lucida console',
];

const MENTION_HREF_PREFIX = 'https://matrix.to/#/';
const ABSOLUTE_HREF_PATTERN = /^(https?:|mailto:)/i;
const RAW_BOUNDARY = '\u0001';

function getStyleProperty(inlineStyle: string, property: string): string | null {
    const pattern = new RegExp(`(?:^|;)\\s*${property}\\s*:\\s*([^;]+)`, 'i');
    const match = pattern.exec(inlineStyle);
    return match ? match[1].trim().toLowerCase() : null;
}

const PRESERVED_WHITE_SPACE_VALUES = new Set(['pre', 'pre-wrap', 'pre-line']);

function nearestWhiteSpace(node: Node): string {
    let current = node.parentElement;
    while (current) {
        const value = getStyleProperty(current.getAttribute('style') || '', 'white-space');
        if (value) {
            return value;
        }
        current = current.parentElement;
    }
    return 'normal';
}

function textForWhiteSpace(raw: string, whiteSpace: string): string {
    const normalized = raw.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    if (!PRESERVED_WHITE_SPACE_VALUES.has(whiteSpace)) {
        return collapseWhitespace(normalized);
    }
    if (whiteSpace === 'pre-line') {
        return normalized
            .split('\n')
            .map(line => line.replace(/[\t\u00A0 ]+/g, ' '))
            .join('\n');
    }
    return normalized;
}

const PUNCTUATION_AFTER_LINK = /^[.,;:!?)\]}'"]/;

function decideBold(tag: string, inlineStyle: string): boolean | null {
    const fontWeight = getStyleProperty(inlineStyle, 'font-weight');
    if (fontWeight) {
        if (fontWeight === 'bold' || fontWeight === 'bolder') return true;
        if (fontWeight === 'normal' || fontWeight === 'lighter') return false;
        const numeric = Number(fontWeight);
        if (!Number.isNaN(numeric)) return numeric >= 600;
    }
    if (tag === 'B' || tag === 'STRONG') return true;
    return null;
}

function decideItalic(tag: string, inlineStyle: string): boolean | null {
    const fontStyle = getStyleProperty(inlineStyle, 'font-style');
    if (fontStyle) {
        if (fontStyle === 'italic' || fontStyle === 'oblique') return true;
        if (fontStyle === 'normal') return false;
    }
    if (tag === 'I' || tag === 'EM') return true;
    return null;
}

function decideStrike(tag: string, inlineStyle: string): boolean | null {
    const decoration = getStyleProperty(inlineStyle, 'text-decoration') ?? getStyleProperty(inlineStyle, 'text-decoration-line');
    if (decoration) {
        if (decoration.includes('line-through')) return true;
        if (decoration === 'none') return false;
    }
    if (tag === 'S' || tag === 'DEL' || tag === 'STRIKE') return true;
    return null;
}

function isMonospaceFontFamily(fontFamily: string | null): boolean {
    return !!fontFamily && MONOSPACE_KEYWORDS.some(keyword => fontFamily.includes(keyword));
}

function decideCode(tag: string, inlineStyle: string): boolean | null {
    if (tag === 'CODE' || tag === 'KBD' || tag === 'SAMP' || tag === 'TT') return true;
    if (isMonospaceFontFamily(getStyleProperty(inlineStyle, 'font-family'))) return true;
    return null;
}

function computeStyleUpdate(el: HTMLElement): Partial<StyleFlags> {
    const inlineStyle = el.getAttribute('style') || '';
    const tag = el.tagName;
    const update: Partial<StyleFlags> = {};

    const bold = decideBold(tag, inlineStyle);
    if (bold !== null) update.bold = bold;

    const italic = decideItalic(tag, inlineStyle);
    if (italic !== null) update.italic = italic;

    const strike = decideStrike(tag, inlineStyle);
    if (strike !== null) update.strike = strike;

    const code = decideCode(tag, inlineStyle);
    if (code !== null) update.code = code;

    return update;
}

type EdgeAdjacency = {before: boolean; after: boolean};

const NO_ADJACENCY: EdgeAdjacency = {before: false, after: false};
const WORD_CHAR_START = /^[\p{L}\p{N}]/u;
const WORD_CHAR_END = /[\p{L}\p{N}]$/u;

function hugWrapSingleLine(text: string, marker: string, adjacency: EdgeAdjacency): string {
    const leadingMatch = /^\s*/.exec(text);
    const trailingMatch = /\s*$/.exec(text);
    const leading = leadingMatch ? leadingMatch[0] : '';
    const trailing = trailingMatch ? trailingMatch[0] : '';
    const core = text.slice(leading.length, text.length - trailing.length);
    if (core === '') {
        return text;
    }
    if (core.startsWith(marker) || core.endsWith(marker)) {
        return text;
    }
    if (adjacency.before && leading === '') {
        return text;
    }
    if (adjacency.after && trailing === '') {
        return text;
    }
    return `${leading}${marker}${core}${marker}${trailing}`;
}

const RAW_SEGMENT = new RegExp(`${RAW_BOUNDARY}([\\s\\S]*?)${RAW_BOUNDARY}`, 'g');

function hugWrapRawAware(text: string, marker: string, adjacency: EdgeAdjacency): string {
    if (!text.includes(RAW_BOUNDARY)) {
        return hugWrapSingleLine(text, marker, adjacency);
    }

    const parts: Array<{raw: boolean; value: string}> = [];
    let lastIndex = 0;
    let m: RegExpExecArray | null;
    RAW_SEGMENT.lastIndex = 0;
    while ((m = RAW_SEGMENT.exec(text)) !== null) {
        parts.push({raw: false, value: text.slice(lastIndex, m.index)});
        parts.push({raw: true, value: m[1]});
        lastIndex = m.index + m[0].length;
    }
    parts.push({raw: false, value: text.slice(lastIndex)});

    const plainIndices = parts.reduce<number[]>((acc, part, index) => {
        if (!part.raw) acc.push(index);
        return acc;
    }, []);
    const firstPlain = plainIndices[0];
    const lastPlain = plainIndices[plainIndices.length - 1];

    return parts
        .map((part, index) => {
            if (part.raw) {
                return RAW_BOUNDARY + part.value + RAW_BOUNDARY;
            }
            const segmentAdjacency: EdgeAdjacency = {
                before: index === firstPlain ? adjacency.before : false,
                after: index === lastPlain ? adjacency.after : false,
            };
            return hugWrapSingleLine(part.value, marker, segmentAdjacency);
        })
        .join('');
}

function hugWrap(text: string, marker: string, adjacency: EdgeAdjacency = NO_ADJACENCY): string {
    if (!text.includes('\n')) {
        return hugWrapRawAware(text, marker, adjacency);
    }
    const lines = text.split('\n');
    return lines
        .map((line, index) => {
            const lineAdjacency: EdgeAdjacency = {
                before: index === 0 ? adjacency.before : false,
                after: index === lines.length - 1 ? adjacency.after : false,
            };
            return hugWrapRawAware(line, marker, lineAdjacency);
        })
        .join('\n');
}

function applyMarkers(
    text: string,
    flags: {bold: boolean; italic: boolean; strike: boolean},
    adjacency: EdgeAdjacency = NO_ADJACENCY
): string {
    let result = text;
    if (flags.strike) result = hugWrap(result, STRIKETHROUGH_MARKER, adjacency);
    if (flags.italic) result = hugWrap(result, ITALIC_MARKER, adjacency);
    if (flags.bold) result = hugWrap(result, BOLD_MARKER, adjacency);
    return result;
}

function applyMarkersWithEmit(
    text: string,
    flags: {bold: boolean; italic: boolean; strike: boolean},
    adjacency: EdgeAdjacency,
    emit: EmitFlag
): string {
    const result = applyMarkers(text, flags, adjacency);
    if (result !== text) {
        emit.emitted = true;
    }
    return result;
}

function siblingElementText(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent || '';
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
        return (node as Element).textContent || '';
    }
    return '';
}

function hasWordCharBefore(el: Element): boolean {
    let node = el.previousSibling;
    while (node) {
        const text = siblingElementText(node);
        if (text !== '') {
            return WORD_CHAR_END.test(text);
        }
        node = node.previousSibling;
    }
    return false;
}

function hasWordCharAfter(el: Element): boolean {
    let node = el.nextSibling;
    while (node) {
        const text = siblingElementText(node);
        if (text !== '') {
            return WORD_CHAR_START.test(text);
        }
        node = node.nextSibling;
    }
    return false;
}

function nextCharAfter(el: Element): string {
    let node = el.nextSibling;
    while (node) {
        const text = siblingElementText(node);
        if (text !== '') {
            return text[0];
        }
        node = node.nextSibling;
    }
    return '';
}

function edgeAdjacencyOf(el: Element): EdgeAdjacency {
    return {before: hasWordCharBefore(el), after: hasWordCharAfter(el)};
}

function isMentionHref(href: string): boolean {
    return href.startsWith(MENTION_HREF_PREFIX);
}

const EMOJI_IMG_PATTERN = /^[\p{Emoji_Presentation}\p{Extended_Pictographic}]+$/u;

function renderImage(el: HTMLElement): string {
    const stringify = (el.getAttribute('data-stringify-emoji') || '').trim();
    if (stringify) {
        return stringify;
    }
    const alt = (el.getAttribute('alt') || '').trim();
    return alt && EMOJI_IMG_PATTERN.test(alt) ? alt : '';
}

const LAYOUT_LONG_CELL_LENGTH = 200;

function cellForcesLayout(cell: Element): boolean {
    if (cell.querySelector('table')) {
        return true;
    }
    const blockCount = cell.querySelectorAll('div, p, h1, h2, h3, h4, h5, h6, ul, ol, blockquote').length;
    if (blockCount > 1) {
        return true;
    }
    return (cell.textContent || '').trim().length > LAYOUT_LONG_CELL_LENGTH;
}

function isLayoutTable(cellRows: Array<Array<Element | null>>): boolean {
    const columns = max(cellRows.map(row => row.length));
    if (columns < 2) {
        return true;
    }
    return cellRows.some(row => row.some(cell => cell !== null && cellForcesLayout(cell)));
}

function renderTable(table: Element, ctx: RenderContext, emit: EmitFlag): string {
    const cellRows = extractTableCellElements(table);
    if (cellRows.length === 0) {
        return '';
    }

    if (isLayoutTable(cellRows)) {
        const joinChar = ctx.insideTableCell ? ' ' : '\n';
        const cellCtx: RenderContext = {...ctx, insideTableCell: true};
        const lines = cellRows
            .map(row =>
                row
                    .map(cell => (cell ? renderChildren(cell, cellCtx, emit).trim() : ''))
                    .filter(text => text !== '')
                    .join(joinChar)
            )
            .filter(line => line !== '');
        return lines.length > 0 ? `\n${lines.join('\n')}\n` : '';
    }

    const rows = cellElementsToText(cellRows);
    if (rows.length < 2 || max(rows.map(row => row.length)) < 2) {
        const lines = rows.map(row => row.filter(Boolean).join(' ')).filter(Boolean);
        return lines.length > 0 ? `\n${lines.join('\n')}\n` : '';
    }
    emit.emitted = true;
    return `\n${formatTableAsCodeBlock(rows)}\n`;
}

const NON_MERGEABLE_TAGS = new Set(['BR', 'PRE', 'TABLE', 'UL', 'OL', 'A', 'IMG']);

function isMergeableRunElement(el: Element): boolean {
    return !NON_MERGEABLE_TAGS.has(el.tagName) && !BLOCK_TAGS.has(el.tagName);
}

function stylesEqual(a: Partial<StyleFlags>, b: Partial<StyleFlags>): boolean {
    return a.bold === b.bold && a.italic === b.italic && a.strike === b.strike && a.code === b.code;
}

function renderMergedRun(group: Element[], ctx: RenderContext, styleUpdate: Partial<StyleFlags>, emit: EmitFlag): string {
    const childCtx: RenderContext = {...ctx, ...styleUpdate};
    const inner = group.map(child => renderChildren(child, childCtx, emit)).join('');
    const adjacency: EdgeAdjacency = {
        before: hasWordCharBefore(group[0]),
        after: hasWordCharAfter(group[group.length - 1]),
    };
    return applyMarkersWithEmit(
        inner,
        {
            bold: !!childCtx.bold && !ctx.bold,
            italic: !!childCtx.italic && !ctx.italic,
            strike: !!childCtx.strike && !ctx.strike,
        },
        adjacency,
        emit
    );
}

function renderChildren(el: Node, ctx: RenderContext, emit: EmitFlag): string {
    const children = Array.from(el.childNodes);
    let out = '';
    let i = 0;

    while (i < children.length) {
        const child = children[i];

        if (child.nodeType === Node.ELEMENT_NODE && isMergeableRunElement(child as Element)) {
            const styleUpdate = computeStyleUpdate(child as HTMLElement);
            const stylesAdded = styleUpdate.bold || styleUpdate.italic || styleUpdate.strike;
            if (stylesAdded && !styleUpdate.code) {
                const group: Element[] = [child as Element];
                let j = i + 1;
                while (
                    j < children.length &&
                    children[j].nodeType === Node.ELEMENT_NODE &&
                    isMergeableRunElement(children[j] as Element) &&
                    stylesEqual(computeStyleUpdate(children[j] as HTMLElement), styleUpdate)
                ) {
                    group.push(children[j] as Element);
                    j += 1;
                }
                if (group.length > 1) {
                    out += renderMergedRun(group, ctx, styleUpdate, emit);
                    i = j;
                    continue;
                }
            }
        }

        out += renderNode(child, ctx, emit);
        i += 1;
    }

    return out;
}

function renderList(listEl: HTMLElement, ctx: RenderContext, depth: number, emit: EmitFlag): string {
    const isOrdered = listEl.tagName === 'OL';
    const lines: string[] = [];
    let index = 0;
    const itemCtx: RenderContext = {...ctx, inListItem: true};

    Array.from(listEl.children).forEach(child => {
        if (child.tagName === 'UL' || child.tagName === 'OL') {
            lines.push(renderList(child as HTMLElement, ctx, depth + 1, emit));
            return;
        }
        if (child.tagName !== 'LI') {
            return;
        }
        index += 1;
        const prefix = isOrdered ? `${index}. ` : '- ';

        const nestedLists: HTMLElement[] = [];
        let ownContent = '';
        child.childNodes.forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE && ((node as HTMLElement).tagName === 'UL' || (node as HTMLElement).tagName === 'OL')) {
                nestedLists.push(node as HTMLElement);
            } else {
                ownContent += renderNode(node, itemCtx, emit);
            }
        });

        const trimmedOwn = collapseBlankLineRuns(ownContent).trim();
        lines.push(`${'  '.repeat(depth)}${prefix}${trimmedOwn}`);
        nestedLists.forEach(nested => {
            lines.push(renderList(nested, ctx, depth + 1, emit));
        });
    });

    return lines.join('\n');
}

function renderLink(el: HTMLElement, ctx: RenderContext, emit: EmitFlag): string {
    const href = el.getAttribute('href') || '';
    if (isMentionHref(href)) {
        return collapseWhitespace(el.textContent || '');
    }

    const styleUpdate = computeStyleUpdate(el);
    const childCtx = {...ctx, ...styleUpdate};
    const inner = renderChildren(el, childCtx, emit);
    const marked = applyMarkersWithEmit(
        inner,
        {
            bold: !!childCtx.bold && !ctx.bold,
            italic: !!childCtx.italic && !ctx.italic,
            strike: !!childCtx.strike && !ctx.strike,
        },
        edgeAdjacencyOf(el),
        emit
    );

    if (!ABSOLUTE_HREF_PATTERN.test(href)) {
        return marked;
    }

    const hrefSuffix = PUNCTUATION_AFTER_LINK.test(nextCharAfter(el)) ? `${href} ` : href;
    const plainText = collapseWhitespace(el.textContent || '').trim();
    const trimmedMarked = marked.trim();

    if (plainText === href.trim() || trimmedMarked === '') {
        return `${RAW_BOUNDARY}${hrefSuffix}${RAW_BOUNDARY}`;
    }

    emit.emitted = true;
    return `${trimmedMarked}${RAW_BOUNDARY} ${hrefSuffix}${RAW_BOUNDARY}`;
}

function renderNode(node: Node, ctx: RenderContext, emit: EmitFlag): string {
    if (node.nodeType === Node.TEXT_NODE) {
        return textForWhiteSpace(node.textContent || '', nearestWhiteSpace(node));
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
        return '';
    }

    const el = node as HTMLElement;
    const tag = el.tagName;

    if (DROP_TAGS.has(tag)) {
        return '';
    }
    if (tag === 'IMG') {
        return renderImage(el);
    }
    if (tag === 'BR') {
        return ctx.inListItem ? ' ' : '\n';
    }
    if (tag === 'PRE') {
        emit.emitted = true;
        return `\n${fenceBlock(el.textContent || '')}\n`;
    }
    if (tag === 'TABLE') {
        return renderTable(el, ctx, emit);
    }
    if (tag === 'UL' || tag === 'OL') {
        const rendered = renderList(el, ctx, 0, emit);
        if (rendered.trim() !== '') {
            emit.emitted = true;
        }
        return `\n${rendered}\n`;
    }
    if (tag === 'A' && el.hasAttribute('href')) {
        return renderLink(el, ctx, emit);
    }

    const styleUpdate = computeStyleUpdate(el);
    if (styleUpdate.code && !ctx.code && !el.textContent?.includes('\n')) {
        const raw = collapseWhitespace(el.textContent || '');
        const result = raw.includes('`') ? raw : hugWrap(raw, CODE_MARKER);
        if (result !== raw) {
            emit.emitted = true;
        }
        return result;
    }

    const childCtx = {...ctx, ...styleUpdate};
    let inner = renderChildren(el, childCtx, emit);
    if (BLOCK_TAGS.has(tag)) {
        inner = `\n${inner}`;
    }

    return applyMarkersWithEmit(
        inner,
        {
            bold: !!childCtx.bold && !ctx.bold,
            italic: !!childCtx.italic && !ctx.italic,
            strike: !!childCtx.strike && !ctx.strike,
        },
        edgeAdjacencyOf(el),
        emit
    );
}

function dropWhitespaceOnlyLines(text: string): string {
    return text
        .split('\n')
        .map(line => (line.trim() === '' ? '' : line))
        .join('\n');
}

function stripRawBoundaries(text: string): string {
    return text.replace(new RegExp(RAW_BOUNDARY, 'g'), '');
}

interface HtmlConversion {
    text: string;
    hasStructure: boolean;
}

function convertHtmlToMarkers(html: string): HtmlConversion {
    const doc = parseHtmlDocument(html);
    const emit: EmitFlag = {emitted: false};
    const rendered = renderChildren(doc.body, EMPTY_CONTEXT, emit);
    const cleaned = dropWhitespaceOnlyLines(collapseBlankLineRuns(rendered)).trim();
    return {text: stripRawBoundaries(cleaned), hasStructure: emit.emitted};
}

export function htmlToMarkers(html: string): string {
    return convertHtmlToMarkers(html).text;
}

function rootHasIdeStyle(el: Element): boolean {
    const style = el.getAttribute('style') || '';
    const fontFamily = getStyleProperty(style, 'font-family');
    return isMonospaceFontFamily(fontFamily);
}

function detectIdeSnippet(clipboard: ClipboardPayload): string | null {
    if (!clipboard.html || !clipboard.text.includes('\n')) {
        return null;
    }
    const doc = parseHtmlDocument(clipboard.html);
    const bodyChildren = Array.from(doc.body.children);
    const root = bodyChildren.length === 1 ? bodyChildren[0] : doc.body;
    if (!rootHasIdeStyle(root)) {
        return null;
    }
    return fenceBlock(clipboard.text);
}

function hasMeaningfulStructure(hasStructure: boolean, converted: string, plainText: string): boolean {
    if (converted.trim() === '') {
        return false;
    }
    if (hasStructure) {
        return true;
    }
    const normalizedConverted = converted.replace(/\s+/g, ' ').trim();
    const normalizedPlain = plainText.replace(/\s+/g, ' ').trim();
    return normalizedConverted !== normalizedPlain;
}

export function convertPaste(clipboard: ClipboardPayload): ConvertPasteResult {
    const tableResult = detectTableWithSource(clipboard);
    if (tableResult) {
        return {text: formatTableAsCodeBlock(tableResult.rows), kind: 'table', tableSource: tableResult.source};
    }

    const ideSnippet = detectIdeSnippet(clipboard);
    if (ideSnippet) {
        return {text: ideSnippet, kind: 'rich'};
    }

    if (clipboard.html) {
        const {text: converted, hasStructure} = convertHtmlToMarkers(clipboard.html);
        if (hasMeaningfulStructure(hasStructure, converted, clipboard.text)) {
            return {text: converted, kind: 'rich'};
        }
    }

    const alignedTable = detectAlignedTextTable(clipboard.text);
    if (alignedTable) {
        return {text: alignedTable, kind: 'table', tableSource: 'text'};
    }

    return {text: clipboard.text, kind: 'plain'};
}
